import { Helmet } from 'react-helmet';
import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { get } from 'lodash-es';
import { useQuery } from 'react-query';

import { withProfile } from 'modules/v2/containers';
import { withAuth } from 'modules/auth/containers';
import { DashboardNavBar } from 'modules/v2/components';
import { SidebarMenu } from 'modules/v2/common/components';
import { getLeadsStatus, updateNotif } from 'modules/api';
import { getData } from 'modules/auth';
import { fetchJourneyTodos } from 'modules/api/common';
import { isWelcomeJourneyEligible } from 'modules/v2/utils/welcomeJourney';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { CloseIconWhite } from 'modules/v2/common/components/SvgIcon';
import io from 'socket.io-client';
import { getRouteLeads } from 'modules/v2/routes/navigation';
import { useHistory } from 'react-router';
import { useMldDealStatus } from 'modules/v2/utils/hooks/useMldDealStatus';
import { useSubscriptionCancellation } from 'modules/v2/utils/hooks/useSubscriptionCancellation';
import { CancellationBanner } from 'modules/v2/common/components/CancellationBanner';

import './styles.css';
import RelativeLoading from 'v2/common/AtomicDesign/RelativeLoading';
import { cn } from 'modules/v2/common/utils/cn';

import env from 'environments';
import { TaskStatusTypes } from './types';
import { TRIAL_EXPIRING_DAYS } from './utils';

const DashboardTemplate = ({
  avatar,
  children,
  title,
  subHeader,
  profile,
  signOut,
  isLoading,
  isError,
  hasNoData,
  navBar,
  titleClass = 'text-2xl font-bold flex-1 mb-6',
  appendSubHeader = false,
  centralized = false,
}) => {
  const { email, firstName } = get(profile, 'data', {});
  const showContent = !isLoading && !hasNoData && !isError;
  const history = useHistory();

  const isStringTitle = typeof title === 'string';
  const isReactElementSubHeader = React.isValidElement(subHeader);

  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [openMobile, setOpenMobile] = useState(false);
  const [welcomeJourneyEligible, setWelcomeJourneyEligible] = useState(false);
  const [hasTrial, setHasTrial] = useState(false);
  const [hasNotif, setHasNotif] = useState(false);
  const [importLeadsNotifMsg, setImportLeadsNotifMsg] = useState('');
  const [remainingDays, setRemainingDays] = useState('');
  const [taskCount, setTaskCount] = useState(0);
  const [completedTaskCount, setCompletedTaskCount] = useState(3);

  const { isTrialing: mldIsTrialing, trialEndDate } = useMldDealStatus();
  const { isScheduledForCancellation, scheduledCancelDate } = useSubscriptionCancellation();

  const { data: journeyTodos, isLoading: isWjLoading } = useQuery(
    'fetchJourneyTodos',
    fetchJourneyTodos,
    {
      enabled: welcomeJourneyEligible,
    },
  );

  useEffect(() => {
    const { userData } = getData();
    if (isWelcomeJourneyEligible(userData)) {
      setWelcomeJourneyEligible(true);
      if (journeyTodos && !isWjLoading) {
        const { data } = journeyTodos;
        setTaskCount(data.length - 1);
        const result = data.reduce(
          (acc, todo) => {
            if (todo?.status === TaskStatusTypes.COMPLETED) {
              acc.completedCount += 1;
            }
            return acc;
          },
          { completedCount: -1 },
        );
        setCompletedTaskCount(result.completedCount);
      }
    } else {
      setWelcomeJourneyEligible(false);
    }
  }, [welcomeJourneyEligible, isWjLoading, journeyTodos, completedTaskCount]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // The trial banner reads the DS member-list deal status (Chargebee-backed)
    // only. The legacy Chargify fallback was removed: the Chargify account is
    // cancelled and GET /payment-chargify/subscriptions answered 500 for every
    // member (hubspot-off-block-3 task 95).
    if (mldIsTrialing && trialEndDate) {
      setHasTrial(true);
      const renewalDate = new Date(trialEndDate);
      const today = new Date();
      const differenceInTime = renewalDate.getTime() - today.getTime();
      setRemainingDays(Math.ceil(differenceInTime / (1000 * 3600 * 24)));
    }
  }, [mldIsTrialing, trialEndDate]);

  useEffect(() => {
    const socket = io(env.SOCKET_IO, { transports: ['websocket'], withCredentials: true });
    let customerId;

    const fetchStatus = async () => {
      const values = await getLeadsStatus();

      customerId = values.data.customerId;

      setImportLeadsNotifMsg(
        `🎉 Your ${values.data.totalImports} leads has been successfully imported. View and manage your new leads`,
      );
      setHasNotif(values.data.isBannerVisible);

      socket.on(`importLeadsStatus ${customerId}`, (data) => {
        setImportLeadsNotifMsg(
          `🎉 Your ${data.totalImports} leads has been successfully imported. View and manage your new leads`,
        );
        setHasNotif(data.isBannerVisible);
      });
    };

    fetchStatus();

    return () => {
      if (customerId) {
        socket.disconnect();
      }
    };
  }, []);

  const handleViewLeadsClick = () => {
    updateNotif().then(() => {
      setHasNotif(false);
      history.push(getRouteLeads());
    });
  };

  const handleCloseBannerClick = () => {
    updateNotif();
    setHasNotif(false);
  };

  const handleOpenMobile = () => {
    setOpenMobile(!openMobile);
  };

  const notificationMessage =
    remainingDays > 0
      ? `🕜 You have ${
          remainingDays > TRIAL_EXPIRING_DAYS ? TRIAL_EXPIRING_DAYS : remainingDays
        } days left to your trial`
      : '';

  const notificationMessageAccess =
    ' ✅ You\'ll unlock access to the entire platform when you finish this "Welcome Journey" that\'s curated with tasks that provide immediate value for you!';

  const progressContent = (
    <div
      className={cn('flex flex-row fixed w-full z-[999] items-start justify-start top-[62px]', {
        'top-[108px]': hasTrial || welcomeJourneyEligible,
      })}
    >
      <div
        className={cn(
          'h-1.5 mr-1 bg-neutral-300 w-[calc(33.3%-1px)]',
          { 'bg-yellow-300': completedTaskCount > 0 },
          { 'w-[calc(33.1%-1px)]': taskCount === 3 && windowSize < 1024 },
          { 'w-[50%]': taskCount === 2 },
        )}
      />
      <div
        className={cn(
          'h-1.5 mr-1 bg-neutral-300 w-[calc(33%-1px)]',
          { 'bg-yellow-300': completedTaskCount > 1 },
          { 'w-[calc(32.4%-1px)]': taskCount === 3 && windowSize < 1024 },
          { 'w-[48%]': taskCount === 2 },
        )}
      />
      <div
        className={cn(
          'h-1.5 bg-neutral-300 w-[calc(33%-1px)]',
          { 'bg-yellow-300': completedTaskCount > 2 },
          { 'w-[calc(32.9%-1px)]': taskCount === 3 && windowSize < 1024 },
        )}
      />
    </div>
  );

  const bannerNotif = () => {
    return (
      <>
        {hasNotif && (
          <div className="sticky gap-4 text-center font-medium top-[62px] bg-neutral-800 w-full text-white h-[46px] z-[998] flex items-center justify-center text-sm pr-[70px] pl-[30px]">
            {importLeadsNotifMsg}
            <Button type="outlined" size="xs" onClick={handleViewLeadsClick}>
              View leads
            </Button>
            <button
              className="absolute right-[32px] top-[10px]"
              aria-label="Close"
              type="button"
              onClick={handleCloseBannerClick}
            >
              <CloseIconWhite />
            </button>
          </div>
        )}
      </>
    );
  };

  const welcomeJourneyTicker = () => {
    return (
      <>
        {welcomeJourneyEligible && (
          <div className="fixed font-medium top-[62px] bg-neutral-800 w-full text-white h-[46px] z-[998] flex items-center text-sm">
            <div className="flex animate-loop-scroll whitespace-nowrap">
              <div className="flex">
                <p className="mr-14">
                  {notificationMessage} {remainingDays > 0 && <span className="mr-14" />}{' '}
                  {notificationMessageAccess}
                </p>
                <p className="mr-14" aria-hidden="true">
                  {notificationMessage} {remainingDays > 0 && <span className="mr-14" />}{' '}
                  {notificationMessageAccess}
                </p>
              </div>
              <div className="flex">
                <p className="mr-14">
                  {notificationMessage} {remainingDays > 0 && <span className="mr-14" />}{' '}
                  {notificationMessageAccess}
                </p>
                <p className="mr-14" aria-hidden="true">
                  {notificationMessage} {remainingDays > 0 && <span className="mr-14" />}{' '}
                  {notificationMessageAccess}
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <style type="text/css">
          {`
            html {
              font-size: 16px;
            }

            html #root {
              font-family: "Figtree", sans-serif!important
            }
          `}
        </style>
      </Helmet>

      {windowSize > 1024 ? (
        <div className="tracking-normal">
          {navBar || (
            <DashboardNavBar
              email={email}
              avatar={avatar}
              username={firstName}
              onLogout={signOut}
              welcomeJourneyEligible={welcomeJourneyEligible}
            />
          )}

          {hasTrial && !welcomeJourneyEligible && (
            <div className="fixed font-medium top-[62px] bg-neutral-800 w-full text-white h-[46px] z-[998] flex items-center justify-center text-sm">
              {notificationMessage}
            </div>
          )}

          {welcomeJourneyTicker()}

          {bannerNotif()}

          {isScheduledForCancellation && scheduledCancelDate && (
            <CancellationBanner scheduledCancelDate={scheduledCancelDate} />
          )}

          {welcomeJourneyEligible && (
            <div>{(taskCount === 2 || taskCount === 3) && progressContent}</div>
          )}

          <aside
            id="sidebar-multi-level-sidebar"
            className={cn(
              'fixed top-[58px] mt-[10px] left-0 z-40 w-sidebar h-screen transition-transform -translate-x-full sm:translate-x-0 text-xs',
              { 'top-[95px] ': hasTrial || hasNotif || welcomeJourneyEligible || isScheduledForCancellation },
            )}
            aria-label="Sidebar"
          >
            <SidebarMenu openMobile />
          </aside>
          <div
            className={cn(
              'relative left-[268px] top-[54px] w-[calc(100%-271px)] min-h-[calc(100vh-62px)] bg-neutral-50 p-[34px] max-sm:w-full max-sm:h-full max-sm:left-0 max-sm:px-8 max-sm:ml-0',
              { 'top-[95px] ': hasTrial || welcomeJourneyEligible || isScheduledForCancellation },
            )}
          >
            <RelativeLoading isLoading={isLoading} className="bg-opacity-60" />
            <div className={cn("w-full h-full mr-auto", centralized && "max-w-[1300px] mx-auto")}>
              {
                appendSubHeader ? (
                  <div className="flex">
                    {isStringTitle && <div className={titleClass}>{title}</div>}
                    {isReactElementSubHeader && subHeader}
                  </div>
                ) : (
                  <>
                    {isStringTitle && <div className={titleClass}>{title}</div>}
                    {isReactElementSubHeader && subHeader}
                  </>
                )
              }
              {showContent && <div className="w-full h-fit">{children}</div>}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          {navBar || (
            <DashboardNavBar
              email={email}
              avatar={avatar}
              username={firstName}
              onLogout={signOut}
              mobileDevice={handleOpenMobile}
              welcomeJourneyEligible={welcomeJourneyEligible}
            />
          )}

          {hasTrial && !welcomeJourneyEligible && (
            <div className="fixed font-medium top-[62px] bg-neutral-800 w-full text-white h-[46px] z-[999] flex items-center justify-center text-sm">
              {notificationMessage}
            </div>
          )}

          {welcomeJourneyTicker()}

          {bannerNotif()}

          {isScheduledForCancellation && scheduledCancelDate && (
            <CancellationBanner scheduledCancelDate={scheduledCancelDate} />
          )}

          {welcomeJourneyEligible && (
            <div>{(taskCount === 2 || taskCount === 3) && progressContent}</div>
          )}
          <SidebarMenu hasTrial={hasTrial} openMobile={openMobile} isScheduledForCancellation={isScheduledForCancellation} />
          <div
            className={cn('relative top-[52px] w-full h-full bg-neutral-50 p-6', {
              'top-[99px] ': hasTrial || welcomeJourneyEligible || isScheduledForCancellation,
            })}
          >
            {
              appendSubHeader ? (
                <div className="flex">
                  {isStringTitle && <div className={titleClass}>{title}</div>}
                  {isReactElementSubHeader && subHeader}
                </div>
              ) : (
                <>
                  {isStringTitle && <div className={titleClass}>{title}</div>}
                  {isReactElementSubHeader && subHeader}
                </>
              )
            }
            {showContent && <div className="w-full h-fit">{children}</div>}
          </div>
        </div>
      )}
    </>
  );
};

export default compose(withProfile, withAuth)(DashboardTemplate);
